import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { leadsAPI, surveyAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Target, Settings, Users, BarChart3, AlertTriangle, CheckCircle,
  Clock, Loader2, User, Building2, Star, MessageSquare, Shield,
  TrendingUp, ArrowUpRight, Eye, UserCheck, Flag, Save, RefreshCw,
  ChevronDown, Calendar, Zap,
} from 'lucide-react';

const RANK_LABELS = { neg: 'NEG - Negotiator', rm: 'RM - Relationship Manager', avp: 'AVP - Area Vice President', svp: 'SVP - Senior Vice President', evp: 'EVP - Executive Vice President' };
const ALL_RANKS = ['neg', 'rm', 'avp', 'svp', 'evp'];
const LEAD_STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700', claimed: 'bg-green-100 text-green-700',
  expired: 'bg-gray-100 text-gray-600', escalated: 'bg-amber-100 text-amber-700',
};

export default function AdminOpenLeads() {
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [performance, setPerformance] = useState(null);
  const [surveys, setSurveys] = useState(null);
  const [flaggedSurveys, setFlaggedSurveys] = useState(null);
  const [assignDialog, setAssignDialog] = useState(null);
  const [assignAgentId, setAssignAgentId] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [resolveDialog, setResolveDialog] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [agents, setAgents] = useState([]);
  const [runningExpiry, setRunningExpiry] = useState(false);
  const [reopening, setReopening] = useState(null);
  const [targetRanksDialog, setTargetRanksDialog] = useState(null);
  const [selectedTargetRanks, setSelectedTargetRanks] = useState([]);
  const [savingTargetRanks, setSavingTargetRanks] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leadsAPI.getDashboard();
      setDashboard(res.data);
      setConfig(res.data.config || {});
    } catch (err) { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, []);

  const loadPerformance = useCallback(async () => {
    try {
      const res = await surveyAPI.getAgentPerformance();
      setPerformance(res.data);
      setAgents(res.data.agents || []);
    } catch (err) { console.error('Failed to load performance:', err); }
  }, []);

  const loadSurveys = useCallback(async () => {
    try {
      const [allRes, flaggedRes] = await Promise.all([surveyAPI.getAll(), surveyAPI.getFlagged()]);
      setSurveys(allRes.data);
      setFlaggedSurveys(flaggedRes.data);
    } catch (err) { console.error('Failed to load surveys:', err); }
  }, []);

  useEffect(() => { loadDashboard(); loadPerformance(); loadSurveys(); }, [loadDashboard, loadPerformance, loadSurveys]);

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await leadsAPI.updateConfig({
        visible_ranks: config.visible_ranks || [],
        lead_expiry_hours: config.lead_expiry_hours || 24,
        escalation_enabled: config.escalation_enabled !== false,
        escalation_fallback: config.escalation_fallback || 'admin_notify',
      });
      toast.success('Configuration saved');
    } catch (err) { toast.error('Failed to save'); }
    finally { setSavingConfig(false); }
  };

  const toggleRank = (rank) => {
    setConfig(prev => {
      const ranks = prev.visible_ranks || [];
      return { ...prev, visible_ranks: ranks.includes(rank) ? ranks.filter(r => r !== rank) : [...ranks, rank] };
    });
  };

  const handleAssign = async () => {
    if (!assignDialog || !assignAgentId) return;
    setAssigning(true);
    try {
      await leadsAPI.assignLead(assignDialog.id, { assign_to_agent_id: assignAgentId, notes: assignNotes });
      toast.success('Lead assigned');
      setAssignDialog(null);
      setAssignAgentId('');
      setAssignNotes('');
      loadDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to assign'); }
    finally { setAssigning(false); }
  };

  const handleResolve = async () => {
    if (!resolveDialog) return;
    try {
      await surveyAPI.resolve(resolveDialog.id, { admin_notes: resolveNotes });
      toast.success('Survey resolved');
      setResolveDialog(null);
      setResolveNotes('');
      loadSurveys();
    } catch { toast.error('Failed to resolve'); }
  };

  const handleRunExpiry = async () => {
    setRunningExpiry(true);
    try {
      const res = await leadsAPI.runExpiry();
      const d = res.data;
      toast.success(`Expiry check complete: ${d.expired || 0} expired, ${d.escalated || 0} escalated`);
      loadDashboard();
    } catch (err) { toast.error('Failed to run expiry check'); }
    finally { setRunningExpiry(false); }
  };

  const handleReopen = async (leadId) => {
    setReopening(leadId);
    try {
      await leadsAPI.reopenLead(leadId);
      toast.success('Lead reopened');
      loadDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reopen'); }
    finally { setReopening(null); }
  };

  const openTargetRanksDialog = (lead) => {
    setTargetRanksDialog(lead);
    setSelectedTargetRanks(lead.target_ranks || []);
  };

  const toggleTargetRank = (rank) => {
    setSelectedTargetRanks(prev =>
      prev.includes(rank) ? prev.filter(r => r !== rank) : [...prev, rank]
    );
  };

  const handleSaveTargetRanks = async () => {
    if (!targetRanksDialog) return;
    setSavingTargetRanks(true);
    try {
      await leadsAPI.setTargetRanks(targetRanksDialog.id, { target_ranks: selectedTargetRanks });
      toast.success(selectedTargetRanks.length > 0 ? `Lead restricted to ${selectedTargetRanks.map(r => r.toUpperCase()).join(', ')}` : 'Lead visible to all ranks');
      setTargetRanksDialog(null);
      loadDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update target ranks'); }
    finally { setSavingTargetRanks(false); }
  };

  const stats = dashboard?.stats || {};

  if (loading) return (
    <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>
  );

  return (
    <div className="space-y-6" data-testid="admin-open-leads">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Target className="h-7 w-7 text-teal-600" /> Open Leads Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Monitor, configure, and manage open leads pool</p>
        </div>
        <Button variant="outline" onClick={() => { loadDashboard(); loadPerformance(); loadSurveys(); }} className="gap-2" data-testid="refresh-all">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Open', value: stats.open || 0, icon: Target, color: 'text-blue-600 bg-blue-50' },
          { label: 'Claimed', value: stats.claimed || 0, icon: CheckCircle, color: 'text-green-600 bg-green-50' },
          { label: 'Expired', value: stats.expired || 0, icon: Clock, color: 'text-gray-600 bg-gray-50' },
          { label: 'Escalated', value: stats.escalated || 0, icon: AlertTriangle, color: 'text-amber-600 bg-amber-50' },
          { label: 'Unassigned Customers', value: stats.unassigned_customers || 0, icon: Users, color: 'text-purple-600 bg-purple-50' },
        ].map((s, i) => (
          <Card key={i}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`h-10 w-10 rounded-lg ${s.color} flex items-center justify-center`}><s.icon className="h-5 w-5" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{s.value}</p><p className="text-xs text-gray-500">{s.label}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-xl">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Leads</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
          <TabsTrigger value="surveys" data-testid="tab-surveys">
            Surveys {flaggedSurveys?.total > 0 && <Badge className="ml-1 bg-red-600 text-white text-xs">{flaggedSurveys.total}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">Configuration</TabsTrigger>
        </TabsList>

        {/* LEADS TAB */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{(dashboard?.leads || []).length} leads total</p>
            <Button variant="outline" size="sm" className="gap-2" onClick={handleRunExpiry} disabled={runningExpiry} data-testid="run-expiry-btn">
              {runningExpiry ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clock className="h-3.5 w-3.5" />}
              Run Expiry Check
            </Button>
          </div>
          <div className="space-y-3">
            {(dashboard?.leads || []).length === 0 ? (
              <div className="text-center py-12 text-gray-500">No leads found</div>
            ) : (dashboard?.leads || []).map(lead => (
              <Card key={lead.id} className="border" data-testid={`admin-lead-${lead.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="flex flex-col items-center gap-1 shrink-0 w-16">
                      <Badge className={`text-xs ${LEAD_STATUS_COLORS[lead.lead_status] || 'bg-gray-100'}`}>{lead.lead_status?.toUpperCase()}</Badge>
                      {lead.age_hours !== undefined && <span className="text-xs text-gray-400">{lead.age_hours < 1 ? `${Math.round(lead.age_hours * 60)}m` : `${lead.age_hours.toFixed(0)}h`}</span>}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{lead.customer?.full_name || 'Unknown'}</p>
                      <p className="text-sm text-gray-500 truncate">{lead.property?.name || 'Property'} - {lead.preferred_date || 'TBD'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {lead.target_ranks && lead.target_ranks.length > 0 && (
                      <Badge className="bg-purple-100 text-purple-700 text-xs">{lead.target_ranks.map(r => r.toUpperCase()).join(', ')}</Badge>
                    )}
                    {lead.claimed_agent && (
                      <span className="text-xs text-gray-500 flex items-center gap-1"><UserCheck className="h-3 w-3" /> {lead.claimed_agent.full_name}</span>
                    )}
                    {lead.lead_status === 'open' && (
                      <>
                        <Button size="sm" variant="ghost" className="text-purple-600 hover:text-purple-700 gap-1" onClick={() => openTargetRanksDialog(lead)} data-testid={`target-ranks-btn-${lead.id}`}>
                          <Shield className="h-3.5 w-3.5" /> Ranks
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setAssignDialog(lead)} data-testid={`assign-btn-${lead.id}`}>Assign</Button>
                      </>
                    )}
                    {(lead.lead_status === 'expired' || lead.lead_status === 'escalated') && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setAssignDialog(lead)} data-testid={`assign-btn-${lead.id}`}>Assign</Button>
                        <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-700" onClick={() => handleReopen(lead.id)} disabled={reopening === lead.id} data-testid={`reopen-btn-${lead.id}`}>
                          {reopening === lead.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                          <span className="ml-1">Reopen</span>
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* PERFORMANCE TAB */}
        <TabsContent value="performance" className="space-y-4">
          <div className="space-y-3">
            {(performance?.agents || []).length === 0 ? (
              <div className="text-center py-12 text-gray-500">No agent data</div>
            ) : (performance?.agents || []).map((agent, i) => (
              <Card key={agent.id} className="border" data-testid={`agent-perf-${agent.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center font-bold text-teal-700 text-sm">
                        {i + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{agent.full_name}</p>
                        <p className="text-xs text-gray-500">{agent.agent_code} - {(agent.agent_rank || 'neg').toUpperCase()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <p className="font-bold text-gray-900">{agent.leads_accepted}</p>
                        <p className="text-xs text-gray-400">Leads</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-gray-900">{agent.avg_response_minutes != null ? `${agent.avg_response_minutes}m` : '-'}</p>
                        <p className="text-xs text-gray-400">Avg Response</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-amber-600 flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />{agent.avg_survey_rating ?? '-'}</p>
                        <p className="text-xs text-gray-400">{agent.survey_count} reviews</p>
                      </div>
                      {agent.negative_feedback_count > 0 && (
                        <Badge className="bg-red-100 text-red-700 text-xs">{agent.negative_feedback_count} flagged</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* SURVEYS TAB */}
        <TabsContent value="surveys" className="space-y-4">
          {flaggedSurveys?.total > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-red-700"><Flag className="h-4 w-4" /> Flagged Reviews ({flaggedSurveys.total})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(flaggedSurveys?.surveys || []).map(s => (
                  <div key={s.id} className="flex items-center justify-between p-3 bg-white rounded-lg border" data-testid={`flagged-survey-${s.id}`}>
                    <div>
                      <p className="text-sm font-medium">{s.agent?.full_name || 'Agent'} <span className="text-gray-400">rated by</span> {s.customer?.full_name || 'Customer'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-amber-500 flex items-center gap-0.5 text-sm"><Star className="h-3.5 w-3.5 fill-amber-400" />{s.average_rating?.toFixed(1)}</span>
                        {s.comments && <span className="text-xs text-gray-500 truncate max-w-xs">"{s.comments}"</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={s.flag_status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{s.flag_status || 'new'}</Badge>
                      {s.flag_status !== 'resolved' && (
                        <Button size="sm" variant="outline" onClick={() => { setResolveDialog(s); setResolveNotes(''); }} data-testid={`resolve-btn-${s.id}`}>Resolve</Button>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader><CardTitle className="text-base">All Completed Surveys ({surveys?.total_completed || 0})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(surveys?.surveys || []).length === 0 ? (
                <p className="text-center py-8 text-gray-500">No surveys completed yet</p>
              ) : (surveys?.surveys || []).map(s => (
                <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`survey-${s.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{s.agent?.full_name} - {s.property?.name}</p>
                    <p className="text-xs text-gray-500">by {s.customer?.full_name} {s.completed_at ? `on ${new Date(s.completed_at).toLocaleDateString()}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-amber-500 flex items-center gap-0.5"><Star className="h-3.5 w-3.5 fill-amber-400" />{s.average_rating?.toFixed(1)}</span>
                    {s.is_flagged && <Badge className="bg-red-100 text-red-700 text-xs">Flagged</Badge>}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONFIG TAB */}
        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Shield className="h-4 w-4" /> Agent Role Visibility</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-500">Select which agent ranks can see and accept open leads:</p>
              {ALL_RANKS.map(rank => (
                <div key={rank} className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50" data-testid={`rank-toggle-${rank}`}>
                  <div>
                    <p className="font-medium text-sm">{RANK_LABELS[rank]}</p>
                  </div>
                  <Switch
                    checked={(config?.visible_ranks || []).includes(rank)}
                    onCheckedChange={() => toggleRank(rank)}
                    data-testid={`rank-switch-${rank}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Settings className="h-4 w-4" /> Lead Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Lead Expiry (hours)</Label>
                  <Input type="number" value={config?.lead_expiry_hours || 24} onChange={e => setConfig(prev => ({ ...prev, lead_expiry_hours: parseInt(e.target.value) || 24 }))} data-testid="expiry-hours" />
                </div>
                <div>
                  <Label className="text-sm">Escalation Fallback</Label>
                  <Select value={config?.escalation_fallback || 'admin_notify'} onValueChange={v => setConfig(prev => ({ ...prev, escalation_fallback: v }))}>
                    <SelectTrigger data-testid="escalation-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin_notify">Notify Admin</SelectItem>
                      <SelectItem value="round_robin">Round Robin Auto-Assign</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Enable Auto-Escalation</Label>
                <Switch checked={config?.escalation_enabled !== false} onCheckedChange={v => setConfig(prev => ({ ...prev, escalation_enabled: v }))} data-testid="escalation-switch" />
              </div>
            </CardContent>
          </Card>
          <Button className="bg-teal-600 hover:bg-teal-700 gap-2" onClick={handleSaveConfig} disabled={savingConfig} data-testid="save-config">
            {savingConfig ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Configuration
          </Button>
        </TabsContent>
      </Tabs>

      {/* Assign Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle>Assign Lead Manually</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Lead: {assignDialog?.customer?.full_name} - {assignDialog?.property?.name}</Label>
            </div>
            <div>
              <Label className="text-sm">Select Agent</Label>
              <Select value={assignAgentId} onValueChange={setAssignAgentId}>
                <SelectTrigger data-testid="assign-agent-select"><SelectValue placeholder="Choose agent..." /></SelectTrigger>
                <SelectContent>
                  {agents.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.full_name} ({a.agent_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Notes (optional)</Label>
              <Textarea value={assignNotes} onChange={e => setAssignNotes(e.target.value)} placeholder="Reason for manual assignment..." data-testid="assign-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog(null)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleAssign} disabled={!assignAgentId || assigning} data-testid="confirm-assign">
              {assigning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={!!resolveDialog} onOpenChange={() => setResolveDialog(null)}>
        <DialogContent data-testid="resolve-dialog">
          <DialogHeader><DialogTitle>Resolve Flagged Review</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Agent: <strong>{resolveDialog?.agent?.full_name}</strong> | Rating: <strong>{resolveDialog?.average_rating?.toFixed(1)}/5</strong></p>
            {resolveDialog?.comments && <p className="text-sm text-gray-500 italic">"{resolveDialog.comments}"</p>}
            <div>
              <Label className="text-sm">Admin Notes</Label>
              <Textarea value={resolveNotes} onChange={e => setResolveNotes(e.target.value)} placeholder="Action taken, findings..." data-testid="resolve-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialog(null)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleResolve} disabled={!resolveNotes} data-testid="confirm-resolve">Resolve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Target Ranks Dialog */}
      <Dialog open={!!targetRanksDialog} onOpenChange={() => setTargetRanksDialog(null)}>
        <DialogContent data-testid="target-ranks-dialog">
          <DialogHeader><DialogTitle>Set Lead Target Ranks</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Lead: <strong>{targetRanksDialog?.customer?.full_name}</strong> - {targetRanksDialog?.property?.name}</p>
            <p className="text-sm text-gray-500">Select which ranking groups can see this lead. Leave empty for all ranks.</p>
            {ALL_RANKS.map(rank => (
              <div key={rank} className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50" data-testid={`target-rank-${rank}`}>
                <p className="font-medium text-sm">{RANK_LABELS[rank]}</p>
                <Switch
                  checked={selectedTargetRanks.includes(rank)}
                  onCheckedChange={() => toggleTargetRank(rank)}
                  data-testid={`target-rank-switch-${rank}`}
                />
              </div>
            ))}
            {selectedTargetRanks.length === 0 && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">No ranks selected = visible to all ranks (based on global config)</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTargetRanksDialog(null)}>Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleSaveTargetRanks} disabled={savingTargetRanks} data-testid="save-target-ranks">
              {savingTargetRanks ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save Target Ranks
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
